import React from 'react'
import cx from 'classnames'
import type { SanityImageSource } from '@sanity/image-url/lib/types/types'

import { Image } from 'src/components/image'
import { builder } from 'src/config/sanityClient'
import { useMediaQuery } from 'src/hooks/useMediaQuery'
import { Serializer } from 'src/utils/serializer'
import BlockContent from 'src/components/global/BlockContent'
import { ButtonBlock } from 'src/components/inline/buttonBlock'
import { CtaAnalyticsContext } from 'src/utils/ctaTracking'
import { Arrow } from 'src/components/svgs'
import { useEmblaCarousel } from 'embla-carousel/react'

type LayoutMode = 'flex' | 'grid'
type LayoutBoxDisplayMode = 'default' | 'carousel'
type LayoutBoxItemsPerSlide = 1 | 2 | 3
type Direction = 'row' | 'row-reverse' | 'column' | 'column-reverse'
type Gap = 'none' | 'sm' | 'md' | 'lg'
type Padding = 'none' | 'sm' | 'md' | 'lg'
type SectionVerticalPadding = Padding | 'extra'
type ContentTone = 'dark' | 'light'
type ResponsiveContentTone = ContentTone | 'same'
type Border = 'none' | 'thin'
type Radius = 'none' | 'sm' | 'md'
type Fit = 'cover' | 'contain'
type AspectRatio = 'auto' | '1-1' | '4-3' | '16-9'
type ButtonAlign = 'left' | 'center' | 'right' | 'stretch'
type LayoutIconSize = 'icon' | 'small' | 'custom'
type LayoutElementType =
  | 'layoutBox'
  | 'layoutText'
  | 'layoutImage'
  | 'layoutIcon'
  | 'layoutButton'
type LayoutSectionMinHeight = number | string

type LayoutElement = {
  _key?: string
  _type: LayoutElementType
}

type LayoutBoxData = LayoutElement & {
  _type: 'layoutBox'
  title?: string
  items?: LayoutElement[]
  displayMode?: LayoutBoxDisplayMode
  itemsPerSlide?: LayoutBoxItemsPerSlide
  layoutMode?: LayoutMode
  columns?: number
  mobileColumns?: number
  direction?: Direction
  mobileDirection?: Direction | 'same'
  gap?: Gap
  alignItems?: string
  justifyContent?: string
  backgroundColor?: string
  contentTone?: ContentTone
  mobileContentTone?: ResponsiveContentTone
  paddingTop?: Padding
  paddingRight?: Padding
  paddingBottom?: Padding
  paddingLeft?: Padding
  border?: Border
  borderRadius?: Radius
  backgroundImage?: LayoutSectionBackgroundImage
  mobileBackgroundImage?: LayoutSectionBackgroundImage
  addOverlay?: boolean
  minHeightDesktop?: LayoutSectionMinHeight
  minHeightMobile?: LayoutSectionMinHeight
}

type LayoutTextData = LayoutElement & {
  _type: 'layoutText'
  text?: any[]
}

type LayoutImageData = LayoutElement & {
  _type: 'layoutImage'
  image?: {
    asset?: {
      _id?: string
    }
  }
  mobileImage?: {
    asset?: {
      _id?: string
    }
  }
  caption?: string
  aspectRatio?: AspectRatio
  objectFit?: Fit
  borderRadius?: Radius
}

type LayoutIconData = LayoutElement & {
  _type: 'layoutIcon'
  image?: {
    assetId?: string
    asset?: {
      _id?: string
      _ref?: string
    }
  }
  caption?: string
  altText?: string
  iconSize?: LayoutIconSize
  maxWidth?: number
}

type LayoutButtonData = LayoutElement & {
  _type: 'layoutButton'
  items?: any[]
  onlyStackButtons?: boolean
  align?: ButtonAlign
}

type LayoutAnalyticsContext = CtaAnalyticsContext

type LayoutSectionBackgroundImage = {
  asset?: {
    _id?: string
    _ref?: string
  }
}

export type LayoutSectionData = {
  _key?: string
  _type: 'layoutSection'
  title?: string
  options?: {
    uid?: string
  }
  items?: LayoutBoxData[]
  layoutMode?: LayoutMode
  columns?: number
  mobileColumns?: number
  direction?: Direction
  gap?: Gap
  showDividers?: boolean
  alignItems?: string
  justifyContent?: string
  backgroundColor?: string
  contentTone?: ContentTone
  mobileContentTone?: ResponsiveContentTone
  backgroundImage?: LayoutSectionBackgroundImage
  mobileBackgroundImage?: LayoutSectionBackgroundImage
  addOverlay?: boolean
  minHeightDesktop?: LayoutSectionMinHeight
  minHeightMobile?: LayoutSectionMinHeight
  paddingY?: SectionVerticalPadding
  paddingX?: Padding
}

const buttonAlignValues: Record<ButtonAlign, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
  stretch: 'stretch',
}

const layoutIconWidthValues: Record<'icon' | 'small', number> = {
  icon: 32,
  small: 120,
}

const fallbackCustomLayoutIconWidth = 32
const maxCustomLayoutIconWidth = 320

const gapValues: Record<Gap, string> = {
  none: '0',
  sm: '1rem',
  md: '1.5rem',
  lg: '2rem',
}

const LAYOUT_SECTION_BACKGROUND_MOBILE_QUERY = '(max-width: 799px)'

type LayoutSectionBackgroundSubscriber = (value: boolean) => void

const layoutSectionBackgroundSubscribers = new Set<
  LayoutSectionBackgroundSubscriber
>()
let layoutSectionBackgroundQuery: MediaQueryList | null = null
let removeLayoutSectionBackgroundListener: (() => void) | null = null
let layoutSectionBackgroundValue = false

const classValue = (value: string) => value.replace(/\s+/g, '-')

const getLayoutIconSize = (iconSize?: LayoutIconSize): LayoutIconSize => {
  if (iconSize === 'small' || iconSize === 'custom') return iconSize

  return 'icon'
}

const getLayoutIconWidth = (data: LayoutIconData, size: LayoutIconSize) => {
  if (size === 'icon' || size === 'small') {
    return layoutIconWidthValues[size]
  }

  const value = Number(data.maxWidth)

  if (!Number.isFinite(value) || value <= 0) {
    return fallbackCustomLayoutIconWidth
  }

  return Math.min(Math.round(value), maxCustomLayoutIconWidth)
}

const getLayoutIconUrl = (imageId: string, width: number) => {
  const imageSrc = imageId as SanityImageSource
  const imageUrl = builder.image(imageSrc)?.width(width * 2)

  return imageUrl ? imageUrl.url() ?? '' : ''
}

const getImageId = (image?: LayoutSectionBackgroundImage) =>
  image?.asset?._id || image?.asset?._ref

const getBackgroundImageIdForViewport = (
  desktopImageId?: string,
  mobileImageId?: string
) => {
  if (
    typeof window !== 'undefined' &&
    'function' === typeof window.matchMedia &&
    window.matchMedia(LAYOUT_SECTION_BACKGROUND_MOBILE_QUERY).matches
  ) {
    return mobileImageId || desktopImageId
  }

  return desktopImageId || mobileImageId
}

const getBackgroundImageUrls = (
  image?: LayoutSectionBackgroundImage,
  width?: number
) => {
  if (!image) {
    return {
      dataSource: '',
      sourceUrl: '',
    }
  }

  const imageSrc = image as SanityImageSource
  const requestedWidth = 'number' === typeof width && width > 0 ? width : 768
  const dataSrc = builder.image(imageSrc)?.width(requestedWidth)

  return {
    dataSource: dataSrc ? dataSrc.url() ?? '' : '',
    sourceUrl: dataSrc ? dataSrc.format('webp').url() ?? '' : '',
  }
}

const useMeasuredElementWidth = <T extends HTMLElement>(
  defaultWidth = 768,
  maxWidth?: number
) => {
  const elementRef = React.useRef<T | null>(null)
  const [width, setWidth] = React.useState(defaultWidth)

  React.useEffect(() => {
    const element = elementRef.current

    if (!element || 'undefined' === typeof ResizeObserver) return

    const updateWidth = (value: number) => {
      if (!value) return

      const nextWidth = Math.ceil(
        Math.min(value * (window.devicePixelRatio || 1), maxWidth || value * 2)
      )

      setWidth(currentWidth =>
        Math.abs(currentWidth - nextWidth) > 40 ? nextWidth : currentWidth
      )
    }

    updateWidth(element.getBoundingClientRect().width)

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0]

      if (!entry) return

      updateWidth(entry.contentRect.width)
    })

    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [maxWidth])

  return [elementRef, width] as const
}

const getMinHeightValue = (value?: LayoutSectionMinHeight) => {
  if (value === undefined || value === null || value === '') return undefined

  if (typeof value === 'number') {
    return `${value}px`
  }

  const trimmedValue = value.trim()

  if (!trimmedValue) return undefined

  if (/^\d+(\.\d+)?$/.test(trimmedValue)) {
    return `${trimmedValue}px`
  }

  if (
    /^\d+(\.\d+)?(px|rem|em|vh|vw|vmin|vmax|svh|lvh|dvh|%)$/i.test(
      trimmedValue
    ) ||
    /^(calc|clamp|min|max)\(.+\)$/i.test(trimmedValue)
  ) {
    return trimmedValue
  }

  return undefined
}

const getColumns = (columns?: number, defaultValue = 2, max?: number) => {
  const value = Number(columns) || defaultValue
  const minValue = Math.max(value, 1)

  return 'number' === typeof max ? Math.min(minValue, max) : minValue
}

const getMobileDirection = (data: LayoutBoxData) => {
  const direction = data.direction || 'column'

  if (!data.mobileDirection || data.mobileDirection === 'same') {
    return direction
  }

  return data.mobileDirection
}

const ensureLayoutSectionBackgroundListener = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  if (layoutSectionBackgroundQuery) return true

  const query = window.matchMedia(LAYOUT_SECTION_BACKGROUND_MOBILE_QUERY)
  const syncBackgroundBreakpoint = () => {
    layoutSectionBackgroundValue = query.matches
    layoutSectionBackgroundSubscribers.forEach(subscriber =>
      subscriber(layoutSectionBackgroundValue)
    )
  }

  layoutSectionBackgroundQuery = query
  layoutSectionBackgroundValue = query.matches

  if ('addEventListener' in query) {
    query.addEventListener('change', syncBackgroundBreakpoint)
    removeLayoutSectionBackgroundListener = () =>
      query.removeEventListener('change', syncBackgroundBreakpoint)

    return true
  }

  query.addListener(syncBackgroundBreakpoint)
  removeLayoutSectionBackgroundListener = () =>
    query.removeListener(syncBackgroundBreakpoint)

  return true
}

const subscribeToLayoutSectionBackground = (
  subscriber: LayoutSectionBackgroundSubscriber
) => {
  if (!ensureLayoutSectionBackgroundListener()) return () => {}

  layoutSectionBackgroundSubscribers.add(subscriber)
  subscriber(layoutSectionBackgroundValue)

  return () => {
    layoutSectionBackgroundSubscribers.delete(subscriber)

    if (
      layoutSectionBackgroundSubscribers.size === 0 &&
      removeLayoutSectionBackgroundListener
    ) {
      removeLayoutSectionBackgroundListener()
      removeLayoutSectionBackgroundListener = null
      layoutSectionBackgroundQuery = null
      layoutSectionBackgroundValue = false
    }
  }
}

const useLayoutSectionBackgroundMobile = () => {
  const [isMobileBackground, setMobileBackground] = React.useState(false)

  React.useEffect(() => {
    return subscribeToLayoutSectionBackground(setMobileBackground)
  }, [])

  return isMobileBackground
}

const LayoutText = ({ data }: { data: LayoutTextData }) => {
  if (!data.text?.length) return null

  return (
    <div className="layout-text">
      <BlockContent blocks={data.text} serializers={Serializer} />
    </div>
  )
}

const LayoutImage = ({ data }: { data: LayoutImageData }) => {
  const { isMobileDevice } = useMediaQuery()
  const desktopImageId = data.image?.asset?._id
  const mobileImageId = data.mobileImage?.asset?._id
  const imageId =
    isMobileDevice && mobileImageId ? mobileImageId : desktopImageId

  if (!imageId) return null

  return (
    <div
      className={cx(
        'layout-image',
        `layout-image--ratio-${data.aspectRatio || 'auto'}`,
        `layout-image--fit-${data.objectFit || 'cover'}`,
        `layout-image--radius-${data.borderRadius || 'none'}`
      )}
    >
      <Image
        imageId={imageId}
        alt={data.caption}
        className="layout-image__asset"
      />
    </div>
  )
}

const LayoutIcon = ({ data }: { data: LayoutIconData }) => {
  const imageId =
    data.image?.assetId || data.image?.asset?._id || data.image?.asset?._ref

  if (!imageId) return null

  const iconSize = getLayoutIconSize(data.iconSize)
  const iconWidth = getLayoutIconWidth(data, iconSize)
  const iconUrl = getLayoutIconUrl(imageId, iconWidth)

  if (!iconUrl) return null

  return (
    <div
      className={cx('layout-icon', `layout-icon--size-${iconSize}`)}
      style={{ width: iconWidth }}
    >
      <img
        alt={data.caption || data.altText || ''}
        className="layout-icon__asset"
        loading="lazy"
        src={iconUrl}
        width={iconWidth}
      />
    </div>
  )
}

const LayoutSectionBackground = ({
  activeImageId,
  alt,
  className,
  desktopImage,
  desktopImageId,
  mobileImage,
  mobileImageId,
  defaultImageWidth = 768,
  maxImageWidth = 1920,
  onError,
  onLoad,
  priority,
  rawStyleOverride,
}: {
  activeImageId?: string
  alt?: string
  className?: string
  desktopImage?: LayoutSectionBackgroundImage
  desktopImageId?: string
  defaultImageWidth?: number
  mobileImage?: LayoutSectionBackgroundImage
  mobileImageId?: string
  maxImageWidth?: number
  onError?: (imageId?: string) => void
  onLoad?: (imageId?: string) => void
  priority?: boolean
  rawStyleOverride?: React.CSSProperties
}) => {
  const [loaded, setLoaded] = React.useState(false)
  const imageRef = React.useRef<HTMLImageElement | null>(null)
  const hasReportedLoad = React.useRef(false)
  const [containerRef, imageWidth] = useMeasuredElementWidth<HTMLDivElement>(
    priority ? 1920 : defaultImageWidth,
    priority ? 1920 : maxImageWidth
  )
  const desktopWidth = priority ? 1920 : imageWidth
  const mobileWidth = priority ? 768 : Math.min(imageWidth, 768)
  const desktopUrls = React.useMemo(
    () => getBackgroundImageUrls(desktopImage, desktopWidth),
    [desktopImage, desktopWidth]
  )
  const mobileUrls = React.useMemo(
    () => getBackgroundImageUrls(mobileImage, mobileWidth),
    [mobileImage, mobileWidth]
  )
  const imageFetchPriority = priority ? 'high' : undefined

  const selectedImageId = React.useMemo(
    () =>
      activeImageId ||
      getBackgroundImageIdForViewport(desktopImageId, mobileImageId),
    [activeImageId, desktopImageId, mobileImageId]
  )

  const handleImageLoad = React.useCallback(() => {
    if (hasReportedLoad.current) return

    hasReportedLoad.current = true
    setLoaded(true)
    onLoad?.(selectedImageId)
  }, [onLoad, selectedImageId])

  const handleImageError = React.useCallback(() => {
    if (hasReportedLoad.current) return

    hasReportedLoad.current = true
    onError?.(selectedImageId)
  }, [onError, selectedImageId])

  React.useEffect(() => {
    hasReportedLoad.current = false
    setLoaded(false)
  }, [desktopUrls.dataSource, mobileUrls.dataSource, selectedImageId])

  React.useEffect(() => {
    const image = imageRef.current

    if ((desktopUrls.dataSource || mobileUrls.dataSource) && image?.complete) {
      if (image.naturalWidth > 0) {
        handleImageLoad()
      } else {
        handleImageError()
      }
    }
  }, [
    desktopUrls.dataSource,
    handleImageError,
    handleImageLoad,
    mobileUrls.dataSource,
    selectedImageId,
  ])

  if (!desktopUrls.dataSource && !mobileUrls.dataSource) return null

  return (
    <div ref={containerRef} className={cx('image__block', className)}>
      <picture>
        {mobileUrls.sourceUrl ? (
          <source
            media={LAYOUT_SECTION_BACKGROUND_MOBILE_QUERY}
            srcSet={mobileUrls.sourceUrl}
            type="image/webp"
          />
        ) : null}
        {mobileUrls.dataSource ? (
          <source
            media={LAYOUT_SECTION_BACKGROUND_MOBILE_QUERY}
            srcSet={mobileUrls.dataSource}
          />
        ) : null}
        {desktopUrls.sourceUrl ? (
          <source
            media="(min-width: 800px)"
            srcSet={desktopUrls.sourceUrl}
            type="image/webp"
          />
        ) : null}
        <img
          ref={imageRef}
          alt={alt}
          style={rawStyleOverride || {}}
          src={desktopUrls.dataSource || mobileUrls.dataSource}
          loading={priority ? 'eager' : 'lazy'}
          {...(imageFetchPriority && { fetchpriority: imageFetchPriority })}
          className={cx('x y object-fit block', {
            'is-loaded': loaded,
          })}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </picture>
    </div>
  )
}

const LayoutBoxBackground = ({
  activeImageId,
  desktopImage,
  desktopImageId,
  mobileImage,
  mobileImageId,
}: {
  activeImageId?: string
  desktopImage?: LayoutSectionBackgroundImage
  desktopImageId?: string
  mobileImage?: LayoutSectionBackgroundImage
  mobileImageId?: string
}) => {
  return (
    <LayoutSectionBackground
      activeImageId={activeImageId}
      alt=""
      className="layout-box__background-image"
      desktopImage={desktopImage}
      desktopImageId={desktopImageId}
      defaultImageWidth={480}
      maxImageWidth={1024}
      mobileImage={mobileImage}
      mobileImageId={mobileImageId}
      rawStyleOverride={{
        objectFit: 'cover',
        objectPosition: 'center center',
      }}
    />
  )
}

const LayoutButton = ({
  data,
  analyticsContext,
}: {
  data: LayoutButtonData
  analyticsContext: LayoutAnalyticsContext
}) => {
  if (!data.items?.length) return null

  const align = data.align || 'left'

  return (
    <div className={cx('layout-button', `layout-button--${align}`)}>
      <ButtonBlock
        data={{
          items: data.items,
          onlyStackButtons: data.onlyStackButtons,
        }}
        analyticsContext={analyticsContext}
        className="layout-button__block"
        style={{
          justifyContent: buttonAlignValues[align],
          marginBottom: 0,
          width: align === 'stretch' ? '100%' : undefined,
        }}
      />
    </div>
  )
}

const LayoutBoxCarousel = ({
  items,
  itemsPerSlide,
  isMobileDevice,
  gap,
  analyticsContext,
  inheritedContentTone,
  inheritedMobileContentTone,
  hasBackground,
  label,
}: {
  items: LayoutElement[]
  itemsPerSlide: LayoutBoxItemsPerSlide
  isMobileDevice: boolean
  gap: Gap
  analyticsContext: LayoutAnalyticsContext
  inheritedContentTone: ContentTone
  inheritedMobileContentTone: ContentTone
  hasBackground: boolean
  label: string
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)
  const [EmblaCarouselViewport, embla] = useEmblaCarousel({
    loop: false,
    containScroll: true,
    align: 'start',
  })
  const slides = React.useMemo(() => {
    const groups: LayoutElement[][] = []

    for (let index = 0; index < items.length; index += itemsPerSlide) {
      groups.push(items.slice(index, index + itemsPerSlide))
    }

    return groups
  }, [items, itemsPerSlide])

  const updateCarouselState = React.useCallback(() => {
    if (!embla) return

    setSelectedIndex(embla.selectedScrollSnap())
    setCanScrollPrev(embla.canScrollPrev())
    setCanScrollNext(embla.canScrollNext())
  }, [embla])

  React.useEffect(() => {
    if (!embla) return

    updateCarouselState()
    embla.on('select', updateCarouselState)
    embla.on('reInit', updateCarouselState)

    return () => {
      embla.off('select', updateCarouselState)
      embla.off('reInit', updateCarouselState)
    }
  }, [embla, updateCarouselState])

  React.useEffect(() => {
    if (!embla) return

    embla.reInit()
    updateCarouselState()
  }, [embla, gap, items.length, itemsPerSlide, updateCarouselState])

  React.useEffect(() => {
    if (!embla) return

    const viewport = embla.containerNode().parentElement
    if (!viewport) return

    if (!isMobileDevice) {
      viewport.style.height = ''
      return
    }

    const activeSlide = embla.slideNodes()[selectedIndex]
    if (!activeSlide) return

    const updateViewportHeight = () => {
      viewport.style.height = `${activeSlide.getBoundingClientRect().height}px`
    }

    updateViewportHeight()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateViewportHeight)
    resizeObserver.observe(activeSlide)

    return () => resizeObserver.disconnect()
  }, [embla, isMobileDevice, selectedIndex, slides.length])

  React.useEffect(() => {
    if (!embla) return

    const viewport = embla.containerNode().parentElement
    if (!viewport) return

    return () => {
      viewport.style.height = ''
    }
  }, [embla])

  return (
    <div className="layout-box__carousel" role="region" aria-label={label}>
      <EmblaCarouselViewport className="layout-box__carousel-viewport">
        <div
          className="layout-box__carousel-track"
          style={{
            marginLeft: gap === 'none' ? 0 : `-${gapValues[gap]}`,
          }}
        >
          {slides.map((slideItems, slideIndex) => (
            <div
              key={slideItems[0]?._key || slideIndex}
              className={cx(
                'layout-box__carousel-slide',
                `layout-box__carousel-slide--tone-${inheritedContentTone}`,
                `layout-box__carousel-slide--mobile-tone-${inheritedMobileContentTone}`,
                {
                  'layout-box__carousel-slide--has-background': hasBackground,
                }
              )}
              role="group"
              aria-label={`Slide ${slideIndex + 1} of ${slides.length}`}
              style={{
                gap: gapValues[gap],
                gridTemplateColumns: `repeat(${itemsPerSlide}, minmax(0, 1fr))`,
                paddingLeft: gapValues[gap],
              }}
            >
              {slideItems.map((item, itemIndex) => (
                <LayoutElement
                  key={item._key || itemIndex}
                  data={item}
                  analyticsContext={analyticsContext}
                  inheritedContentTone={inheritedContentTone}
                  inheritedMobileContentTone={inheritedMobileContentTone}
                />
              ))}
            </div>
          ))}
        </div>
      </EmblaCarouselViewport>
      {slides.length > 1 ? (
        <div className="layout-box__carousel-controls">
          <button
            type="button"
            className="layout-box__carousel-control no-style"
            aria-label="Previous slide"
            disabled={!canScrollPrev}
            onClick={() => embla?.scrollPrev()}
          >
            <Arrow />
          </button>
          <span
            className="layout-box__carousel-position"
            aria-live="polite"
            aria-atomic="true"
          >
            {selectedIndex + 1} / {slides.length}
          </span>
          <button
            type="button"
            className="layout-box__carousel-control layout-box__carousel-control--next no-style"
            aria-label="Next slide"
            disabled={!canScrollNext}
            onClick={() => embla?.scrollNext()}
          >
            <Arrow />
          </button>
        </div>
      ) : null}
    </div>
  )
}

const LayoutBox = ({
  data,
  analyticsContext,
  inheritedContentTone,
  inheritedMobileContentTone,
}: {
  data: LayoutBoxData
  analyticsContext: LayoutAnalyticsContext
  inheritedContentTone?: ContentTone
  inheritedMobileContentTone?: ContentTone
}) => {
  const { isMobileDevice } = useMediaQuery()
  const items = data.items || []
  const hasCarousel = data.displayMode === 'carousel' && items.length > 1
  const itemsPerSlide = data.itemsPerSlide > 1 ? data.itemsPerSlide : 1
  const layoutMode = data.layoutMode || 'flex'
  const direction = data.direction || 'column'
  const mobileDirection = getMobileDirection(data)
  const columns = getColumns(data.columns, 2, 6)
  const mobileColumns = getColumns(data.mobileColumns, 1, 6)
  const gap = data.gap || 'md'
  const alignItems = data.alignItems || 'stretch'
  const justifyContent = data.justifyContent || 'flex-start'
  const hasOwnContentTone = Boolean(data.contentTone)
  const desktopContentTone = data.contentTone || inheritedContentTone || 'dark'
  const mobileContentTone =
    data.mobileContentTone && data.mobileContentTone !== 'same'
      ? data.mobileContentTone
      : hasOwnContentTone
      ? desktopContentTone
      : inheritedMobileContentTone || desktopContentTone
  const paddingTop = data.paddingTop || 'none'
  const paddingRight = data.paddingRight || 'none'
  const paddingBottom = data.paddingBottom || 'none'
  const paddingLeft = data.paddingLeft || 'none'
  const border = data.border || 'none'
  const borderRadius = data.borderRadius || 'none'
  const desktopBackgroundImageId = getImageId(data.backgroundImage)
  const mobileBackgroundImageId = getImageId(data.mobileBackgroundImage)
  const desktopBackgroundImage =
    data.backgroundImage || data.mobileBackgroundImage
  const mobileBackgroundImage =
    data.mobileBackgroundImage || data.backgroundImage
  const activeBackgroundImageId = getBackgroundImageIdForViewport(
    desktopBackgroundImageId,
    mobileBackgroundImageId
  )
  const hasBackgroundImage = Boolean(activeBackgroundImageId)
  const minHeightValue = getMinHeightValue(
    isMobileDevice
      ? data.minHeightMobile || data.minHeightDesktop
      : data.minHeightDesktop || data.minHeightMobile
  )
  const boxStyle = {
    gap: hasCarousel ? 0 : gapValues[gap],
    ...(minHeightValue ? { minHeight: minHeightValue } : {}),
  } as React.CSSProperties
  const boxAnalyticsContext = {
    ...analyticsContext,
    heading: data.title || analyticsContext.heading,
  }

  return (
    <div
      className={cx(
        'layout-box',
        hasCarousel
          ? 'layout-box--carousel'
          : [
              `layout-box--${layoutMode}`,
              `layout-box--columns-${columns}`,
              `layout-box--mobile-columns-${mobileColumns}`,
              `layout-box--direction-${direction}`,
              `layout-box--mobile-direction-${mobileDirection}`,
              `layout-box--gap-${gap}`,
              `layout-box--align-${classValue(alignItems)}`,
              `layout-box--justify-${classValue(justifyContent)}`,
            ],
        `layout-box--tone-${desktopContentTone}`,
        `layout-box--desktop-tone-${desktopContentTone}`,
        `layout-box--mobile-tone-${mobileContentTone}`,
        `layout-box--padding-top-${paddingTop}`,
        `layout-box--padding-right-${paddingRight}`,
        `layout-box--padding-bottom-${paddingBottom}`,
        `layout-box--padding-left-${paddingLeft}`,
        `layout-box--border-${border}`,
        `layout-box--radius-${borderRadius}`,
        {
          'layout-box--has-background': hasBackgroundImage,
          'layout-box--with-overlay': hasBackgroundImage && data.addOverlay,
        },
        data.backgroundColor
      )}
      style={boxStyle}
    >
      {hasBackgroundImage ? (
        <div className="layout-box__background" aria-hidden="true">
          <LayoutBoxBackground
            activeImageId={activeBackgroundImageId}
            desktopImage={desktopBackgroundImage}
            desktopImageId={desktopBackgroundImageId || mobileBackgroundImageId}
            mobileImage={mobileBackgroundImage}
            mobileImageId={mobileBackgroundImageId || desktopBackgroundImageId}
          />
        </div>
      ) : null}
      {hasBackgroundImage && data.addOverlay ? (
        <div className="layout-box__overlay" aria-hidden="true" />
      ) : null}
      {hasCarousel ? (
        <LayoutBoxCarousel
          items={items}
          itemsPerSlide={isMobileDevice ? 1 : itemsPerSlide}
          isMobileDevice={isMobileDevice}
          gap={gap}
          analyticsContext={boxAnalyticsContext}
          inheritedContentTone={desktopContentTone}
          inheritedMobileContentTone={mobileContentTone}
          hasBackground={hasBackgroundImage}
          label={data.title ? `${data.title} carousel` : 'Content carousel'}
        />
      ) : (
        items.map((item, index) => (
          <LayoutElement
            key={item._key || index}
            data={item}
            analyticsContext={boxAnalyticsContext}
            inheritedContentTone={desktopContentTone}
            inheritedMobileContentTone={mobileContentTone}
          />
        ))
      )}
    </div>
  )
}

const LayoutElement = ({
  data,
  analyticsContext,
  inheritedContentTone,
  inheritedMobileContentTone,
}: {
  data: LayoutElement
  analyticsContext: LayoutAnalyticsContext
  inheritedContentTone?: ContentTone
  inheritedMobileContentTone?: ContentTone
}) => {
  switch (data._type) {
    case 'layoutBox':
      return (
        <LayoutBox
          data={data as LayoutBoxData}
          analyticsContext={analyticsContext}
          inheritedContentTone={inheritedContentTone}
          inheritedMobileContentTone={inheritedMobileContentTone}
        />
      )
    case 'layoutText':
      return <LayoutText data={data as LayoutTextData} />
    case 'layoutImage':
      return <LayoutImage data={data as LayoutImageData} />
    case 'layoutIcon':
      return <LayoutIcon data={data as LayoutIconData} />
    case 'layoutButton':
      return (
        <LayoutButton
          data={data as LayoutButtonData}
          analyticsContext={analyticsContext}
        />
      )
    default:
      return null
  }
}

export const LayoutSection = ({
  data,
  priorityImage = false,
}: {
  data: LayoutSectionData
  priorityImage?: boolean
}) => {
  const { isMobileDevice } = useMediaQuery()
  const isMobileBackground = useLayoutSectionBackgroundMobile()
  const {
    addOverlay,
    backgroundColor,
    backgroundImage,
    columns,
    mobileColumns,
    items = [],
    layoutMode = 'grid',
    mobileBackgroundImage,
  } = data
  const direction = data.direction || 'row'
  const gap = data.gap || 'md'
  const showDividers = Boolean(data.showDividers)
  const alignItems = data.alignItems || 'stretch'
  const justifyContent = data.justifyContent || 'flex-start'
  const paddingY = data.paddingY || 'md'
  const paddingX = data.paddingX || 'md'
  const desktopContentTone = data.contentTone || 'dark'
  const mobileContentTone =
    data.mobileContentTone && data.mobileContentTone !== 'same'
      ? data.mobileContentTone
      : desktopContentTone
  const desktopColumns = getColumns(columns)
  const resolvedMobileColumns = getColumns(mobileColumns, 1, 4)
  const desktopBackgroundImageId = getImageId(backgroundImage)
  const mobileBackgroundImageId = getImageId(mobileBackgroundImage)
  const desktopBackgroundImage = backgroundImage || mobileBackgroundImage
  const mobileResponsiveBackgroundImage =
    mobileBackgroundImage || backgroundImage
  const resolvedMinHeight = isMobileBackground
    ? data.minHeightMobile || data.minHeightDesktop
    : data.minHeightDesktop || data.minHeightMobile
  const resolvedBackgroundImage = isMobileBackground
    ? mobileBackgroundImage || backgroundImage
    : backgroundImage || mobileBackgroundImage
  const resolvedBackgroundImageId = isMobileBackground
    ? mobileBackgroundImageId || desktopBackgroundImageId
    : desktopBackgroundImageId || mobileBackgroundImageId
  const hasBackgroundImage = Boolean(
    resolvedBackgroundImage && resolvedBackgroundImageId
  )
  const [
    loadedPriorityBackgroundId,
    setLoadedPriorityBackgroundId,
  ] = React.useState<string | undefined>()
  const shouldWaitForPriorityBackground = Boolean(
    priorityImage && hasBackgroundImage && resolvedBackgroundImageId
  )
  const priorityBackgroundLoaded =
    !shouldWaitForPriorityBackground ||
    loadedPriorityBackgroundId === resolvedBackgroundImageId

  const minHeightValue = getMinHeightValue(resolvedMinHeight)
  const sectionStyle = {
    ...(minHeightValue
      ? {
          minHeight: minHeightValue,
        }
      : {}),
  } as React.CSSProperties
  const itemsStyle = {
    gap: gapValues[gap],
    gridTemplateColumns:
      layoutMode === 'grid'
        ? `repeat(${
            isMobileDevice ? resolvedMobileColumns : desktopColumns
          }, minmax(0, 1fr))`
        : undefined,
  } as React.CSSProperties
  const analyticsContext = {
    module: 'Layout section',
    heading: data.title || 'Layout section',
  }

  return (
    <section
      id={data.options?.uid}
      className={cx(
        'layout-section',
        `layout-section--${layoutMode}`,
        `layout-section--columns-${desktopColumns}`,
        `layout-section--mobile-columns-${resolvedMobileColumns}`,
        `layout-section--direction-${direction}`,
        `layout-section--gap-${gap}`,
        `layout-section--align-${classValue(alignItems)}`,
        `layout-section--justify-${classValue(justifyContent)}`,
        `layout-section--padding-y-${paddingY}`,
        `layout-section--padding-x-${paddingX}`,
        `layout-section--tone-${desktopContentTone}`,
        `layout-section--desktop-tone-${desktopContentTone}`,
        `layout-section--mobile-tone-${mobileContentTone}`,
        {
          'layout-section--dividers': showDividers,
          'layout-section--has-background': hasBackgroundImage,
          'layout-section--priority-background': shouldWaitForPriorityBackground,
          'layout-section--priority-background-loaded':
            shouldWaitForPriorityBackground && priorityBackgroundLoaded,
          'layout-section--with-overlay': hasBackgroundImage && addOverlay,
        },
        backgroundColor
      )}
      data-module={data._type}
      style={sectionStyle}
    >
      {hasBackgroundImage ? (
        <div className="layout-section__background" aria-hidden="true">
          <LayoutSectionBackground
            activeImageId={resolvedBackgroundImageId}
            desktopImage={desktopBackgroundImage}
            desktopImageId={desktopBackgroundImageId || mobileBackgroundImageId}
            mobileImage={mobileResponsiveBackgroundImage}
            mobileImageId={mobileBackgroundImageId || desktopBackgroundImageId}
            alt=""
            className="layout-section__background-image"
            priority={priorityImage}
            onError={
              shouldWaitForPriorityBackground
                ? failedImageId => {
                    setLoadedPriorityBackgroundId(
                      failedImageId || resolvedBackgroundImageId
                    )
                  }
                : undefined
            }
            onLoad={
              shouldWaitForPriorityBackground
                ? loadedImageId => {
                    setLoadedPriorityBackgroundId(
                      loadedImageId || resolvedBackgroundImageId
                    )
                  }
                : undefined
            }
            rawStyleOverride={{
              objectFit: 'cover',
              objectPosition: 'center center',
            }}
          />
        </div>
      ) : null}
      {hasBackgroundImage && addOverlay ? (
        <div className="layout-section__overlay" aria-hidden="true" />
      ) : null}
      <div className="layout-section__inner container--l mxa">
        <div className="layout-section__items" style={itemsStyle}>
          {items.map((item, index) => (
            <LayoutBox
              key={item._key || index}
              data={item}
              analyticsContext={analyticsContext}
              inheritedContentTone={desktopContentTone}
              inheritedMobileContentTone={mobileContentTone}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export default LayoutSection
